import Grid from '@mui/material/Grid';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDealContext } from '../../../utils/context/DealContext';
import { FiltersDropdown } from '../components/FiltersDropdown';
import { SectionTitle } from '../components/SectionTitle';
import { SelectedFilters } from '../components/SelectedFilters';
import {
  Filters,
  getFilterOptions,
  SupplementalAdQualityField,
  type SupplementalAdQualityFormState,
} from '../constants';

export const LanguagesSection = () => {
  const { useLanguagesFetch } = useDealContext();
  const { setValue, control } = useFormContext<SupplementalAdQualityFormState>();

  const observableLanguagesState = useWatch({ control, name: 'languages' });
  const languagesOp = observableLanguagesState?.op ?? 'current';
  const languagesIds = observableLanguagesState?.ids ?? [];

  const { t } = useTranslation();
  const filterOptions = useMemo(() => getFilterOptions(t), [t]);
  const { languages, isLanguagesLoading, refetchLanguages } = useLanguagesFetch();

  useEffect(() => {
    if (languagesOp === Filters.ALLOW) {
      refetchLanguages();
    }
  }, [refetchLanguages, languagesOp]);

  const handleFilterTypeChange = useCallback(
    (_event, value) => {
      setValue(SupplementalAdQualityField.LANGUAGES_IDS, []);
      setValue(SupplementalAdQualityField.LANGUAGES_OP, value);
    },
    [setValue]
  );

  const handleChange = useCallback(
    (value: string[]) => {
      setValue(SupplementalAdQualityField.LANGUAGES_IDS, value);
    },
    [setValue]
  );

  return (
    <>
      <SectionTitle title={t('Languages')} />
      <Grid container spacing={1}>
        <Grid size={6}>
          <RHFAutoComplete
            name={SupplementalAdQualityField.LANGUAGES_OP}
            textFieldProps={{ margin: 'dense' }}
            disableClearable={true}
            getOptionLabel={option => filterOptions[option]?.name || option}
            options={Object.keys(filterOptions)}
            onChange={handleFilterTypeChange}
            fullWidth
            data-test="saq-languages"
          />
        </Grid>
        {languagesOp === Filters.ALLOW && (
          <Grid size={6}>
            <FiltersDropdown
              options={languages}
              loading={isLanguagesLoading}
              selectedOptions={languagesIds}
              placeholder={t('[ Search A Creative Types ]')}
              onChange={handleChange}
              dataTest="languages-search"
            />
          </Grid>
        )}
      </Grid>
      {languagesIds.length > 0 && (
        <SelectedFilters
          selectedOptions={languagesIds}
          handleChange={handleChange}
          options={languages}
          title={`${t('Languages')} [ ${languagesIds.length} ]`}
          filtersTitle={filterOptions.allow.name}
        />
      )}
    </>
  );
};
